/**
 * Puts the affirmations someone picked into their calendar.
 *
 * Same approach as downloadIcs in lib/utils.ts (used by the team and samen
 * meditations): build an .ics and hand it to the browser. It shows up as an
 * appointment, and unlike an in-app notification it still arrives when the app
 * is closed.
 */

/** Escapes the characters an ICS field can't carry literally. */
function icsText(value: string) {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

export function downloadAffirmationsIcs({
  theme,
  day,
  lines,
  time,
}: {
  theme: string;
  day: number;
  /** The affirmations, in the order they appear on the page. */
  lines: string[];
  /** "HH:MM", local time. */
  time: string;
}) {
  const [hours, minutes] = time.split(':').map(Number);
  const start = new Date();
  start.setHours(hours, minutes, 0, 0);
  // if that moment has passed today, put it in tomorrow's calendar instead
  if (start.getTime() <= Date.now()) start.setDate(start.getDate() + 1);
  const end = new Date(start.getTime() + 15 * 60 * 1000);

  const fmt = (d: Date) =>
    d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

  const title = `${theme} - Dag ${day} Affirmatie`;
  const description = icsText(lines.join('\n'));

  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Hartzorg//Challenge//NL',
    'BEGIN:VEVENT',
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:${icsText(title)}`,
    `DESCRIPTION:${description}`,
    `UID:${crypto.randomUUID()}@hartzorg.app`,
    `DTSTAMP:${fmt(new Date())}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.replace(/[^a-zA-Z0-9 ]/g, '')}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
